import React from 'react';
import PropTypes from 'prop-types';
import { observer, inject } from 'mobx-react';
import { withNamespaces, Trans } from 'react-i18next';
import { getWhitelabelConfig, inWhitelabel } from 'core/whitelabel';
import { staticURL } from 'core/url';

import DocumentHead from 'legacyComponents/documentHead';
import Header from 'apps/components/header/header';
import Banner from 'components/Banner';

import { html } from 'libs/editor/serializers/html';
import { EditorStore } from 'libs/editor/store';
import { Editor } from 'libs/editor';

import { MainWrapper, Divider, Center } from 'legacyComponents/styled/lib';
import { CardSection } from 'legacyComponents/layout/cardSection';
import { ProjectCard } from 'legacyComponents/cards/types/project';
import { ProfileCard } from 'legacyComponents/cards/types/profile';
import { StoryCard } from 'legacyComponents/cards/types/story';

import { SinglePageMicroSiteManager } from 'apps/microSite/singlePage/appManager';
import { StoryAppManager } from 'apps/story/main/appManager';
import { StoryIndicator } from 'apps/story/views/storyIndicator';
import { StoryWrapper, Article } from 'apps/story/styles';
import { BottomStickyWrapper, CtaButton } from 'apps/home/landing/styles';

import styled from 'styled-components';
import { media } from 'legacyComponents/styled/styleUtils';

@withNamespaces()
@inject('routerStore', 'clientStore')
@observer
export class StorySinglePageMicroSite extends React.Component {
  UNSAFE_componentWillMount() {
    if (this.props.whitelabelConfig.id !== 'viralresponse') {
      this.appManager = new StoryAppManager(null, this.props);
      this.sectionsManager = new SinglePageMicroSiteManager(null, this.props);
    }
  }

  signUp = () => {
    const { clientStore } = this.props;
    clientStore.routerStore.navigate('home.signUp', {
      next: 'home',
    });
  };

  render() {
    const { clientStore, t } = this.props;
    const whitelabelConfig = getWhitelabelConfig();
    const { loading, story } = this.appManager;
    if (loading) {
      return <StoryIndicator />;
    }
    if (story === null) {
      return (
        <>
          <Header />
          <MainWrapper marginTop='102px' marginBottom='10px'>
            <Banner variant='warning'>
              <Trans>
                No Story found. Please ensure that a Story exists at the
                specified &quot;storySpace&quot; and &quot;storySlug&quot;.
                Thanks!
              </Trans>
            </Banner>
          </MainWrapper>
        </>
      );
    }
    // FIXME - look into avoiding store creation during render
    const store = new EditorStore({
      initialValue: html.deserialize(story.body || '<p></p>'),
      formatting: 'advanced',
    });

    const sections = [];
    whitelabelConfig.pages.home.homeSections.forEach(section => {
      if (section === 'featuredProjects') {
        sections.push(
          <CardSection
            title={t(whitelabelConfig.pages.home.featuredProjects.title)}
            subTitle=' '
            ownerName={whitelabelConfig.shortTitle || whitelabelConfig.title}
            cardApp={this.sectionsManager.featuredProjectsConn.arrayApp}
            CardComponent={ProjectCard}
            noDataCardType='project'
            first={
              whitelabelConfig.pages.home.homeSections.indexOf(
                'featuredProjects',
              ) === 0
            }
            owner
            isWhitelabel
            gridColumns={3}
          />,
        );
      }
      if (section === 'projects') {
        sections.push(
          <CardSection
            title={t('Projects')}
            tooltip={t('Create new project')}
            subTitle={' '}
            ownerName={whitelabelConfig.shortTitle || whitelabelConfig.title}
            cardApp={this.sectionsManager.projectsConn.arrayApp}
            CardComponent={ProjectCard}
            noDataCardType='project'
            first={
              whitelabelConfig.pages.home.homeSections.indexOf('projects') === 0
            }
            owner
            isWhitelabel
            gridColumns={3}
          />,
        );
      }
      if (
        section === 'stories' &&
        this.sectionsManager.storiesConn.arrayApp.length
      ) {
        sections.push(
          <CardSection
            title={t('Stories')}
            tooltip={t('Tell a story')}
            subTitle={' '}
            ownerName=''
            cardApp={this.sectionsManager.storiesConn.arrayApp}
            CardComponent={StoryCard}
            noDataCardType='story'
            first={
              whitelabelConfig.pages.home.homeSections.indexOf('stories') === 0
            }
            owner
            isWhitelabel
            gridColumns={2}
          />,
        );
      }
      if (
        section === 'organisations' &&
        this.sectionsManager.initiativesConn.arrayApp.length
      ) {
        sections.push(
          <CardSection
            title={t('Organisations')}
            subTitle={' '}
            ownerName={whitelabelConfig.shortTitle || whitelabelConfig.title}
            cardApp={this.sectionsManager.initiativesConn.arrayApp}
            CardComponent={ProfileCard}
            noDataCardType='profile'
            first={
              whitelabelConfig.pages.home.homeSections.indexOf(
                'organisations',
              ) === 0
            }
            last
            owner
            isWhitelabel
            gridColumns={3}
          />,
        );
      }
      if (
        section === 'profiles' &&
        this.sectionsManager.profilesConn.arrayApp.length
      ) {
        sections.push(
          <CardSection
            title='Members'
            subTitle={' '}
            ownerName={whitelabelConfig.shortTitle || whitelabelConfig.title}
            cardApp={this.sectionsManager.profilesConn.arrayApp}
            CardComponent={ProfileCard}
            noDataCardType='organisation'
            first={
              whitelabelConfig.pages.home.homeSections.indexOf('profiles') === 0
            }
            last={true}
            owner={true}
            isWhitelabel={true}
            gridColumns={3}
          />,
        );
      }
    });

    return (
      <>
        <DocumentHead
          title={whitelabelConfig.shortTitle || whitelabelConfig.title}
          description={whitelabelConfig.description}
          imagePath={whitelabelConfig.logo}
        />
        <Header />
        <Wrapper>
          <Article>
            <LogoWhiteLabel
              src={inWhitelabel() ? staticURL(whitelabelConfig.logo) : ''}
            />
            <Title>
              <Trans>{whitelabelConfig.title}</Trans>
            </Title>
            <SubTitle>
              <Trans>{whitelabelConfig.subTitle}</Trans>
            </SubTitle>
            <StoryWrapper margin='40px auto 0 auto'>
              <Editor store={store} />
            </StoryWrapper>
            <Divider width='100%' margin='20px auto 40px auto' />
          </Article>
          {!clientStore.signedInProfile && (
            <BottomStickyWrapper>
              <Center>
                <CtaButton
                  onClick={this.signUp}
                  text={
                    <Trans>
                      {whitelabelConfig.pages.home.cta.signedOut.text}
                    </Trans>
                  }
                />
              </Center>
            </BottomStickyWrapper>
          )}
          {sections}
        </Wrapper>
      </>
    );
  }
}

const Wrapper = styled(MainWrapper)`
  margin-top: 102px;
  margin-bottom: 40px;
  ${media.s`
    margin-top: 82px;
  `};
`;

const LogoWhiteLabel = styled.img`
  display: block;
  width: 100px;
  height: 100px;
  margin: 0px auto;
  ${media.s`
    width: 80px;
    height: 80px;
  `};
`;

const Title = styled.h1`
  text-align: center;
  margin: 20px 0;
  font-size: 1.4em;
  ${media.s`
    font-size: 1.2em;
  `};
`;

const SubTitle = styled.p`
  text-align: center;
  font-style: italic;
  font-size: 17px;
  color: #3d3d3d;
  line-height: 1.6;
  text-align: center;
  ${media.s`
    font-size: 15px;
  `};
`;

StorySinglePageMicroSite.propTypes = {
  params: PropTypes.object,
  routeName: PropTypes.string,
  routerStore: PropTypes.object,
  whitelabelConfig: PropTypes.object,
  clientStore: PropTypes.object,
  t: PropTypes.func,
};
